import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { apiClient } from '@/services/api'
import type { RulesDescription, SeriesFormat, LadderRules, Sport } from '@/types/api'
import { Icon, ICONS } from '@/lib/icons'

interface SeriesRulesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  format: SeriesFormat
  ladderRules?: LadderRules
  sport?: Sport
}

export function SeriesRulesDialog({
  open,
  onOpenChange,
  format,
  ladderRules,
  sport
}: SeriesRulesDialogProps) {
  const { t } = useTranslation()
  const [rules, setRules] = useState<RulesDescription | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchRules = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.getSeriesRules({
        format,
        ladderRules,
        sport
      })
      setRules(response.rules)
    } catch (err) {
      setError(t('series.rules.error'))
      // Error logged for debugging - consider replacing with proper error tracking service in production
      // eslint-disable-next-line no-console
      console.error('Error fetching rules:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && format !== 'SERIES_FORMAT_UNSPECIFIED') {
      fetchRules()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, format, ladderRules, sport])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon icon={ICONS.info} width={20} height={20} />
            {loading ? t('series.rules.loading') : rules?.title || t('series.rules.title')}
          </DialogTitle>
          {rules?.summary && (
            <DialogDescription>
              {rules.summary}
            </DialogDescription>
          )}
        </DialogHeader>

        <DialogBody className="px-4 sm:px-6">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-4 bg-destructive-muted text-destructive rounded-lg">
              <Icon icon={ICONS.alertCircle} width={20} height={20} />
              <span>{error}</span>
            </div>
          )}

          {rules && !loading && (
            <div className="space-y-6">
              {/* Rules List */}
              <div className="bg-muted/30 rounded-lg border p-4 space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Icon icon={ICONS.checkCircle} width={20} height={20} className="text-success" />
                  {t('series.rules.title')}
                </h3>
                <ul className="space-y-2">
                  {rules.rules.map((rule, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-info font-bold min-w-[24px]">
                        {index + 1}.
                      </span>
                      <span className="text-muted-foreground">{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Examples */}
              {rules.examples && rules.examples.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Icon icon={ICONS.info} className="w-5 h-5 text-info" />
                    {t('series.rules.examples')}
                  </h3>
                  <div className="space-y-4">
                    {rules.examples.map((example, index) => (
                      <div
                        key={index}
                        className="border-l-4 border-info bg-info-muted p-4 rounded-r-lg text-info-muted-foreground"
                      >
                        <p className="font-medium mb-1">
                          {example.scenario}
                        </p>
                        <p className="text-sm">
                          <span className="font-semibold">{t('series.rules.result')}:</span> {example.outcome}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="outline" className="w-full sm:w-auto">
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
